import { Language } from '@newo/localization';
import {
	type IntlTelInputI18n,
	type PhoneInputElementProps,
	PhoneInput as SharedPhoneInput
} from '@newo/ui';
import type { Iso2 } from 'intl-tel-input/data';
import { en, es, it } from 'intl-tel-input/i18n';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { type FC, useMemo } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';

interface PhoneInputProps {
	className?: string;
	name?: string;
	value: string;
	initialValue?: string;
	onBlur?: () => void;
	onChange: (value: string) => void;
	error?: string;
	description?: string;
	inputProps?: PhoneInputElementProps;
	onChangeValidity?: (isValid: boolean) => void;
	dropdownContainer?: HTMLElement | null;
}

const getIntlTelInputI18n = (language: string): IntlTelInputI18n => {
	switch (language) {
		case Language.Es:
			return es;
		case Language.It:
			return it;
		default:
			return en;
	}
};

const detectInitialCountry = (phoneValue: string | undefined): Iso2 => {
	if (!phoneValue) {
		return 'us';
	}

	return (parsePhoneNumberFromString(phoneValue)?.country?.toLowerCase() ??
		'us') as Iso2;
};

const PhoneInput: FC<PhoneInputProps> = ({
	className,
	name,
	value,
	initialValue,
	onBlur,
	onChange,
	error,
	description,
	inputProps,
	onChangeValidity,
	dropdownContainer
}) => {
	const { localization } = useHostContext();
	const phoneValue = value || initialValue;

	const i18n = useMemo(
		() => getIntlTelInputI18n(localization.language),
		[localization.language]
	);
	const initialCountry = useMemo(
		() => detectInitialCountry(phoneValue),
		[phoneValue]
	);

	const label = useMemo(
		() => (
			<span className="![font-family:var(--font-body)]">
				{localization.translate(
					'creator',
					'customerIdentification.customer_phone'
				)}
			</span>
		),
		[localization]
	);

	return (
		<SharedPhoneInput
			className={className}
			description={description}
			dropdownContainer={dropdownContainer}
			error={error}
			i18n={i18n}
			initialCountry={initialCountry}
			initialValue={phoneValue}
			inputProps={inputProps}
			label={label}
			name={name}
			onBlur={onBlur}
			onChange={onChange}
			onChangeValidity={onChangeValidity}
			value={value}
		/>
	);
};

export default PhoneInput;
